/**
 * The hub card, open while you stand on the hub, under its beacon (the world's QR code floating over
 * the pad, drawn by the world): share or copy the world's link, join another world by its code, or
 * create one of your own (a guest is offered an account instead). The planet above it stays free:
 * walking off the hub closes it.
 */
import { isGuest } from '@bramvasterd/terrella-protocol';
import { useState, type JSX } from 'react';

import { requestLobby } from '@game/session/lobby';
import { closePanel } from '@game/view/panels';
import { t } from '@i18n/index';
import { ShareButtons } from '@ui/components/ShareButtons';
import { Sheet } from '@ui/components/Sheet';
import { CreateForm, JoinForm } from '@ui/components/WorldForms';
import { useGame, useGameState, useLocale } from '@ui/hooks';

import './hub.css';

export function HubPanel(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const world = useGameState((s) => s.world);
  const me = useGameState((s) => s.me);
  const lobby = useGameState((s) => s.lobby);
  const [hint, setHint] = useState<string | null>(null);
  if (!world) return null;
  const error = hint ?? (lobby.error ? t(`refused.${lobby.error}` as 'refused.network') : null);

  return (
    <Sheet id="hub" title={t('hub.title')} onClose={() => closePanel(game)} className="hub" free>
      <p className="note">{t('hub.invite-hint')}</p>
      <ShareButtons world={world} />
      <h3>{t('welcome.join')}</h3>
      <JoinForm onHint={setHint} />
      <h3>{t('welcome.create')}</h3>
      {me && isGuest(me.id) ? (
        <>
          <p className="note">{t('hub.guest-create')}</p>
          <button
            type="button"
            className="btn"
            onClick={() => requestLobby(game, { kind: 'register' })}
          >
            {t('welcome.register')}
          </button>
        </>
      ) : (
        <CreateForm onHint={setHint} />
      )}
      {error && (
        <p className="note warn" role="alert">
          {error}
        </p>
      )}
    </Sheet>
  );
}
